<template>
    <div class="grid grid-cols-4 gap-16">
        <div class="col-span-3">
            <div class="flex justify-between items-center">
                <div class="flex items-center">
                    <img :src="logo" :alt="`${contract.name}-logo`" class="w-64 h-64 mr-16" />
                    <p class="text-heading-1">{{ textCount }}</p>
                </div>
            </div>
            <div class="flex justify-between mt-16">
                <p v-if="!onlyOptionalFields">{{ $t('multi-posting.campaigns.form.channel_details_description') }}</p>
                <p v-else>{{ $t('multi-posting.campaigns.form.optional_channel_details_description') }}</p>
                <p v-if="!onlyOptionalFields" class="cursor-pointer select-none" @click="showOptional = !showOptional">
                    (<span class="underline">{{ textOptionalFields }}</span>)
                </p>
            </div>
            <ChannelRequirements :contractId="contract.contract_id" :fields="formFields" :showAll="showOptional" />
        </div>
        <div class="col-span-1">
            <slot name="basket"></slot>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState} from "pinia";
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import SvgIcon from "../ui/SvgIcon.vue";
import ChannelRequirements from "./form/ChannelRequirements.vue";
import moment from "moment";

export default {
    name: 'CampaignFormPostingRequirements',
    components: { ChannelRequirements, SvgIcon },
    data() {
        return {
            showOptional: false,
            onlyOptionalFields: false,
            selectedContractIdIndex: 0,
            fields: [],
        }
    },
    computed: {
        ...mapState(useMultiPostingStore, ['selectedItems', 'contracts', 'postingRequirementForms', 'postingRequirementFields']),
        logo() {
            const contract = this.contracts.find(x => x.contract_id === this.contract.contract_id)
            return (contract) ? contract.logo_square_url : ""
        },
        textCount() {
            return this.$t('multi-posting.campaigns.form.channel_details_count', {
                current: this.selectedContractIdIndex + 1,
                total: this.contractIds.length,
                name: this.contract.name
            });
        },
        textOptionalFields() {
            if (this.showOptional) {
                return this.$t('multi-posting.campaigns.form.channel_details_optional.hide');
            } else {
                return this.$t('multi-posting.campaigns.form.channel_details_optional.show');
            }
        },
        selectedContracts() {
            return this.selectedItems.filter(item => item.type === 'contract');
        },
        contract() {
            return this.selectedContracts.find(x => x.contract_id === this.contractIds[this.selectedContractIdIndex]);
        },
        contractIds() {
            return [...new Set(this.selectedContracts.map(({ contract_id }) => contract_id))];
        },
        formFields() {
            return this.postingRequirementFields[this.contract.contract_id];
        },
        form() {
            return this.postingRequirementForms[this.contract.contract_id];
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, ['setValidatingPostingRequirements', 'setPostingRequirementFormErrors',
            'fetchContractOptions', 'savePostingRequirementsData']),
        next() {
            this.setValidatingPostingRequirements(true);
            this.form.resetErrors();
            this.validatePostingRequirements()
                .then(() => {
                    this.savePostingRequirements()
                        .then(() => {
                            if (this.selectedContractIdIndex !== this.contractIds.length - 1) {
                                this.selectedContractIdIndex++;
                            } else {
                                this.$emit('next');
                            }
                            this.setValidatingPostingRequirements(false)
                        })
                        .catch((error) => {
                            if (error.response.data) {
                                this.form._handleError(error)
                            }
                            this.setValidatingPostingRequirements(false)
                        });
                })
                .catch(errors => {
                    this.setValidatingPostingRequirements(false)
                    this.setPostingRequirementFormErrors({ contractId: this.contract.contract_id, errors })
                    this.form.setErrors(errors);
                    this.$forceUpdate();
                });
        },
        previous() {
            if (this.selectedContractIdIndex !== 0) {
                this.selectedContractIdIndex--;
            } else {
                this.$emit('previous');
            }
        },
        ruleOperator(value, rule, label) {
            const buildResult = (valid, message) => {
                return {
                    valid: valid,
                    message: !valid ? message : ''
                }
            }
            switch (rule['rule']) {
                case 'int':
                    // string to integer
                    return buildResult(Number.isInteger(parseInt(value)), `The ${label} must be a number`)
                case 'float':
                    value = parseFloat(value)
                    return buildResult(Number(value) === value && (value % 1 === 0 || value % 1 !== 0), `The ${label} is not a valid number.`)
                case 'regex':
                    let regexString = rule.data;
                    let flags = "";

                    // Extracting flags
                    const flagMatch = regexString.match(/[gimuy]*$/);
                    if (flagMatch) {
                        flags = flagMatch[0];
                    }

                    // Removing flags from the regex string + leading and trailing slashes
                    regexString = regexString.replace(/[gimuy]*$/, "").replace(/^\//, '').replace(/\/$/, '');
                        return buildResult(new RegExp(regexString, flags).test(value.trim()), `The ${label} does not have the correct format.`)
                case 'minlength':
                        const minLength = parseInt(rule.data)
                        return buildResult(value === null || value.length >= minLength, `The ${label} must have at least ${minLength} characters.`)
                case 'maxlength':
                case 'maxitems':
                    const maxLength = parseInt(rule.data)
                    const ruleType = rule['rule'] === 'maxitems' ? 'items' : 'characters'
                    return buildResult(value !== null && value.length <= maxLength, `The ${label} cannot be more than ${maxLength} ${ruleType}.`)
                case 'date':
                    return buildResult(moment(value, rule.data.toUpperCase()).isValid(), `The ${label} is not a valid date.`)
                case 'email':
                    return buildResult(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), `The ${label} is not a valid email.`)
                case 'url':
                    return buildResult(/^(http|https):\/\/[^ "]+$/.test(value), `The ${label} is not a valid URL.`)
                default:
                    return buildResult(true, "Rule not handled")
            }
        },
        validatePostingRequirements() {
            let fields = this.formFields;
            return new Promise((resolve, reject) => {
                let errors = {};
                for (const [fieldName, value] of Object.entries(this.form.data)) {
                    const formField = fields.find(x => x.name === fieldName);

                    // skip value if display rule is not set
                    if (formField.display_rules !== null && !this.displayRuleIsSet(formField)) {
                        continue;
                    }

                    // check if field is required
                    if (formField.required
                        && (!formField.display_rules || this.displayRuleIsSet(formField))
                        && (value === null || value === '' || value.length === 0)
                    ) {
                        errors[fieldName] = [`The ${formField.label} field is required.`];
                        continue;
                    }

                    // check if field is valid based on custom rules
                    let rules = fields.filter(x => x.name === fieldName)[0].rules;
                    if (rules && value) {
                        rules.forEach(rule => {
                            const result = this.ruleOperator(value, rule, formField.label)
                            if (!result.valid) {
                                errors[fieldName] = [result.message ?? `The ${formField.label} field is invalid.`];
                            }
                        });
                    }
                }

                if (Object.keys(errors).length) {
                    reject(errors);
                } else {
                    resolve();
                }
            });
        },
        displayRuleIsSet(field) {
            if (!field.display_rules) return false;
            let isRequired = false;
            field.display_rules.show.forEach(rule => {
                // required if facet is set
                isRequired = !!this.form[rule.facet] && this.validRuleOperator(this.form[rule.facet], rule);
            });
            return isRequired;
        },
        validRuleOperator(value, rule) {
            switch (rule.op) {
                case 'in':
                    return rule.value.includes(value);
                case 'notempty':
                    return !!value;
                case 'equal':
                    return value === rule.value;
                case 'contains':
                    return value.includes(rule.value);
                default:
                    return false;
            }
        },
        async savePostingRequirements() {
            return this.form.submit(data => {
                let filteredData = {};
                // Creates a deep copy of the data object
                let formState = JSON.parse(JSON.stringify(data)) || {};
                formState._options = {};
                const formFields = this.formFields;

                for (let key in data) {
                    const formField = formFields.find(x => x.name === key);
                    const hiddenField = formField.display_rules !== null && !this.displayRuleIsSet(formField)
                    const requiredField = this.postingRequirementFields[this.contract.contract_id].filter(x => x.name === key)[0].required
                    const notNullField = data[key] !== null

                    if (!hiddenField && (requiredField || notNullField)) {
                        filteredData[key] = data[key];
                    }
                }

                if (formFields) {
                    for (let i = 0; i < formFields.length; i++) {
                        const formField = formFields[i];
                        if (formField.type === 'AUTOCOMPLETE') {
                            formState._options[formField.name] = formField.options;
                        }
                    }
                }

                return this.savePostingRequirementsData({
                    campaignId: this.$route.query.campaignId,
                    contractId: this.contract.contract_id,
                    data: {
                        filteredData,
                        formState: JSON.stringify(formState)
                    }
                });
            });
        },
    },
    watch: {
        'selectedContractIdIndex'() {
            if ((this.selectedContractIdIndex !== this.contractIds.length - 1) && !this.contract) this.next();
        },
    },
    beforeMount() {
        // skip step if no contract is selected
        if (!this.contract) this.$emit('next');

        bus.addEventListener('postingRequirementsNext', this.next);
        bus.addEventListener('postingRequirementsPrevious', this.previous);

        if (this.formFields.filter(x => x.required).length === 0) {
            this.onlyOptionalFields = true;
            this.showOptional = true;
        }
    },
    beforeRouteLeave() {
        this.form.resetErrors();
    },
    beforeUnmount() {
        bus.removeEventListener('postingRequirementsNext', this.next);
        bus.removeEventListener('postingRequirementsPrevious', this.previous);
    }
}
</script>
